/**
 * Transaction process graph for plain inquiries:
 *   - default-inquiry
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  INQUIRE_WITHOUT_PAYMENT: 'transition/inquire-without-payment',
  DECLINE_BUYER_REQUEST: 'transition/decline-buyer-request',
  SIGNED_BY_BUYER: 'transition/signed-by-buyer',
  SIGNED_BY_BROKER: 'transition/signed-by-broker',
  COMPLETE: 'transition/complete',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  FREE_INQUIRY: 'free-inquiry',
  WAITING_FOR_BUYER_SIGNATURE: 'waiting-for-buyer-signature',
  BUYER_REQUEST_DECLINED: 'buyer-request-declined',
  WAITING_FOR_BROKER_SIGNATURE: 'waiting-for-broker-signature',
  DELIVERED: 'delivered',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  id: 'default-inquiry/release-1',

  initial: states.INITIAL,

  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE_WITHOUT_PAYMENT]: states.FREE_INQUIRY,
      },
    },
    [states.FREE_INQUIRY]: {
      on: {
        [transitions.WAITING_FOR_BUYER_SIGNATURE]: states.WAITING_FOR_BUYER_SIGNATURE,
        [transitions.DECLINE_BUYER_REQUEST]: states.BUYER_REQUEST_DECLINED,
      },
    },
    [states.WAITING_FOR_BUYER_SIGNATURE]: {
      on: {
        [transitions.SIGNED_BY_BUYER]: states.WAITING_FOR_BROKER_SIGNATURE,
      },
    },
    [states.WAITING_FOR_BROKER_SIGNATURE]: {
      on: {
        [transitions.SIGNED_BY_BROKER]: states.SIGNED_BY_BROKER,
      },
    },
    [states.SIGNED_BY_BROKER]: {
      on: {
        [transitions.COMPLETE]: states.DELIVERED,
      },
    },
    [states.BUYER_REQUEST_DECLINED]: {},
    [states.DELIVERED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
export const isRelevantPastTransition = transition => {
  return [
    transitions.INQUIRE_WITHOUT_PAYMENT,
    transitions.WAITING_FOR_BUYER_SIGNATURE,
    transitions.DECLINE_BUYER_REQUEST,
    transitions.SIGNED_BY_BUYER,
    transitions.SIGNED_BY_BROKER,
    transitions.COMPLETE,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
// NOTE: this functions is added just for the sake of consistency
export const isCustomerReview = transition => {
  return false;
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
// NOTE: this functions is added just for the sake of consistency
export const isProviderReview = transition => {
  return false;
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
// NOTE: this functions is added just for the sake of consistency
export const isPrivileged = transition => {
  return false;
};

// Check when transaction is completed (inquiry completed)
export const isCompleted = transition => {
  return [transitions.COMPLETE].includes(transition);
};

// Check when transaction is refunded (inquiry did not happen)
export const isRefunded = transition => {
  return false;
};

export const statesNeedingProviderAttention = [
states.FREE_INQUIRY,
states.WAITING_FOR_BUYER_SIGNATURE,
states.WAITING_FOR_BROKER_SIGNATURE,
states.SIGNED_BY_BROKER,
];
